import { CATEGORIES, CATEGORY_RULES } from '../data/mockData'
import { calcTax, mileageAllowance, homeOfficeAllowance, fmt, getCurrentQuarter } from './tax'

// ── CSV export ──
export function exportTransactionsCSV(transactions, mileageTrips, homeOffice) {
  const allCats = CATEGORIES
  const rows = [
    ['Date','Description','Merchant','Type','Amount (£)','Category','Category Label','HMRC Rule','VAT Receipt','Categorised At','Source']
  ]
  transactions.forEach(tx => {
    const cat = allCats.find(c => c.id === tx.category)
    const rule = CATEGORY_RULES[tx.category]
    rows.push([
      tx.date,
      `"${tx.description}"`,
      tx.merchant,
      tx.type,
      tx.amount.toFixed(2),
      tx.category || 'uncategorised',
      cat?.label || '',
      rule?.note || 'Standard',
      tx.has_vat_receipt ? 'Yes' : 'No',
      tx.categorisedAt || '',
      tx.categorisedBy || '',
    ])
  })

  // Mileage trips
  rows.push([])
  rows.push(['--- MILEAGE TRIPS ---'])
  rows.push(['Date','From','To','Miles','Purpose','Allowance (£)'])
  mileageTrips.forEach(t => {
    const allowance = Number(t.miles) <= 10000 ? Number(t.miles) * 0.45 : 10000 * 0.45 + (Number(t.miles) - 10000) * 0.25
    rows.push([t.date, t.from, t.to, t.miles, `"${t.purpose}"`, allowance.toFixed(2)])
  })

  // Home office
  rows.push([])
  rows.push(['--- HOME OFFICE ---'])
  const rate = homeOffice.hoursPerMonth >= 101 ? 26 : homeOffice.hoursPerMonth >= 51 ? 18 : 10
  const homeTotal = homeOffice.months * rate
  rows.push(['Months worked from home','Hours/month','Monthly rate (£)','Total allowance (£)'])
  rows.push([homeOffice.months, homeOffice.hoursPerMonth, rate, homeTotal])

  const csv = rows.map(r => r.join(',')).join('\n')
  downloadFile(csv, `quarterly-transactions-${today()}.csv`, 'text/csv')
}

// ── P&L summary CSV ──
export function exportPLSummary(transactions, mileageTrips, homeOffice) {
  const quarter = getCurrentQuarter()
  const income   = transactions.filter(t => t.type === 'income'  && t.category && t.category !== 'personal').reduce((s,t) => s + t.amount, 0)
  const expenses = transactions.filter(t => t.type === 'expense' && t.category && t.category !== 'personal').reduce((s,t) => s + t.amount, 0)
  const mileAdj  = mileageAllowance(mileageTrips.reduce((s,t) => s + Number(t.miles), 0))
  const homeAdj  = homeOfficeAllowance(homeOffice.months, homeOffice.hoursPerMonth)
  const totalExp = expenses + mileAdj + homeAdj
  const { profit, incomeTax, ni, total } = calcTax(income * 4, totalExp * 4)

  // Income breakdown
  const incomeByCategory = {}
  transactions.filter(t => t.type === 'income' && t.category && t.category !== 'personal').forEach(tx => {
    incomeByCategory[tx.category] = (incomeByCategory[tx.category] || 0) + tx.amount
  })

  // Expense breakdown
  const expenseByCategory = {}
  transactions.filter(t => t.type === 'expense' && t.category && t.category !== 'personal').forEach(tx => {
    // Apply 50% rule for meals
    const cat = CATEGORIES.find(c => c.id === tx.category)
    const amt = tx.category === 'exp_meals' ? tx.amount * 0.5 : tx.amount
    expenseByCategory[tx.category] = (expenseByCategory[tx.category] || 0) + amt
  })

  const rows = [
    [`QUARTERLY — P&L SUMMARY`],
    [`Period: ${quarter.label} 2025/26 (${quarter.start} to ${quarter.end})`],
    [`Generated: ${new Date().toLocaleDateString('en-GB')}`],
    [`Note: Annualised figures used for tax estimate`],
    [],
    ['INCOME'],
    ['Category','Quarter (£)','Annual Est. (£)'],
    ...Object.entries(incomeByCategory).map(([catId, amt]) => {
      const cat = CATEGORIES.find(c => c.id === catId)
      return [cat?.label || catId, amt.toFixed(2), (amt * 4).toFixed(2)]
    }),
    ['TOTAL INCOME', income.toFixed(2), (income * 4).toFixed(2)],
    [],
    ['EXPENSES'],
    ['Category','Quarter (£)','Annual Est. (£)','HMRC Note'],
    ...Object.entries(expenseByCategory).map(([catId, amt]) => {
      const cat = CATEGORIES.find(c => c.id === catId)
      const rule = CATEGORY_RULES[catId]
      return [cat?.label || catId, amt.toFixed(2), (amt * 4).toFixed(2), rule?.note || '']
    }),
    ['Mileage Allowance (HMRC)', mileAdj.toFixed(2), (mileAdj * 4).toFixed(2), '45p/25p HMRC rate'],
    ['Home Office Allowance (HMRC)', homeAdj.toFixed(2), (homeAdj * 4).toFixed(2), 'HMRC flat rate'],
    ['TOTAL EXPENSES', totalExp.toFixed(2), (totalExp * 4).toFixed(2)],
    [],
    ['TAX ESTIMATE (ANNUALISED)'],
    ['Net Profit', profit.toFixed(2)],
    ['Income Tax', incomeTax.toFixed(2)],
    ['National Insurance (Class 2 & 4)', ni.toFixed(2)],
    ['TOTAL TAX ESTIMATE', total.toFixed(2)],
    [],
    ['DISCLAIMER: This is an estimate only. Consult an accountant before filing.'],
  ]

  const csv = rows.map(r => Array.isArray(r) ? r.join(',') : r).join('\n')
  downloadFile(csv, `quarterly-pl-summary-${today()}.csv`, 'text/csv')
}

// ── Accountant pack — full summary as structured text ──
export function exportAccountantPack(transactions, mileageTrips, homeOffice, profile) {
  const quarter = getCurrentQuarter()
  const income   = transactions.filter(t => t.type === 'income'  && t.category && t.category !== 'personal').reduce((s,t) => s + t.amount, 0)
  const expenses = transactions.filter(t => t.type === 'expense' && t.category && t.category !== 'personal').reduce((s,t) => s + t.amount, 0)
  const mileAdj  = mileageAllowance(mileageTrips.reduce((s,t) => s + Number(t.miles), 0))
  const homeAdj  = homeOfficeAllowance(homeOffice.months, homeOffice.hoursPerMonth)
  const totalExp = expenses + mileAdj + homeAdj
  const { profit, incomeTax, ni, total } = calcTax(income * 4, totalExp * 4)

  const uncatCount  = transactions.filter(t => !t.category).length
  const receiptsUp  = transactions.filter(t => t.has_vat_receipt).length
  const expCount    = transactions.filter(t => t.type === 'expense').length

  const lines = [
    `QUARTERLY — ACCOUNTANT HANDOFF PACK`,
    `${'='.repeat(50)}`,
    ``,
    `CLIENT: ${profile?.full_name || 'Not set'}`,
    `BUSINESS: ${profile?.business_name || 'Not set'}`,
    `UTR: ${profile?.utr || 'Not provided'}`,
    `PERIOD: ${quarter.label} 2025/26 (${quarter.start} — ${quarter.end})`,
    `GENERATED: ${new Date().toLocaleDateString('en-GB')} ${new Date().toLocaleTimeString('en-GB')}`,
    ``,
    `DATA QUALITY`,
    `${'─'.repeat(30)}`,
    `Total transactions imported: ${transactions.length}`,
    `Categorised: ${transactions.length - uncatCount} (${Math.round(((transactions.length - uncatCount)/transactions.length)*100)}%)`,
    `Uncategorised: ${uncatCount}`,
    `Expense receipts uploaded: ${receiptsUp} of ${expCount} (${Math.round((receiptsUp/expCount)*100)}%)`,
    ``,
    `INCOME SUMMARY`,
    `${'─'.repeat(30)}`,
    `Quarter income: £${income.toFixed(2)}`,
    `Annualised: £${(income * 4).toFixed(2)}`,
    ``,
    `EXPENSE SUMMARY`,
    `${'─'.repeat(30)}`,
    `Quarter business expenses: £${expenses.toFixed(2)}`,
    `Mileage allowance (HMRC): £${mileAdj.toFixed(2)}`,
    `Home office allowance (HMRC): £${homeAdj.toFixed(2)}`,
    `Total allowable deductions: £${totalExp.toFixed(2)}`,
    ``,
    `TAX ESTIMATE (ANNUALISED)`,
    `${'─'.repeat(30)}`,
    `Annualised net profit: £${profit.toFixed(2)}`,
    `Estimated income tax: £${incomeTax.toFixed(2)}`,
    `Estimated NI (Class 2 & 4): £${ni.toFixed(2)}`,
    `TOTAL ESTIMATED TAX: £${total.toFixed(2)}`,
    ``,
    `TRANSACTION DETAIL`,
    `${'─'.repeat(30)}`,
    ...transactions.map(tx => {
      const cat = CATEGORIES.find(c => c.id === tx.category)
      return `${tx.date}  ${tx.description.padEnd(30)} £${tx.amount.toFixed(2).padStart(8)}  ${(cat?.label || 'UNCATEGORISED').padEnd(25)} ${tx.has_vat_receipt ? 'RECEIPT' : 'NO RECEIPT'}`
    }),
    ``,
    `MILEAGE LOG`,
    `${'─'.repeat(30)}`,
    ...mileageTrips.map(t => `${t.date}  ${t.from} → ${t.to}  ${t.miles} miles  ${t.purpose}`),
    ``,
    `NOTES`,
    `${'─'.repeat(30)}`,
    `• Meals & Entertainment: 50% HMRC limit applied automatically`,
    `• Mileage: HMRC 45p/mile (first 10,000), 25p/mile thereafter`,
    `• Home office: HMRC flat rate applied`,
    `• This is an estimate. Actual liability may vary.`,
    ``,
    `Generated by Quarterly — MTD ITSA Ready`,
    `Built by a Chartered Accountant (ACCA)`,
  ]

  downloadFile(lines.join('\n'), `quarterly-accountant-pack-${today()}.txt`, 'text/plain')
}

function today() {
  return new Date().toISOString().slice(0,10)
}

function downloadFile(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
